import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useQuery } from 'react-query';
import FormCostiCorso from '../../../components/FormCostiCorso';
import TitlePage from '../../../components/TitlePage';
import { CourseAttributes, CourseRequest } from '../../../interfaces/courses';
import { CourseStatus } from '../../../interfaces/commons';
import { useAuth } from '../../../contexts/Auth';
import {
  getEntityBalanceTotals,
  isCourseBalanceEnabledForEdit,
} from '../../../libs/utils/helpers';
import { ParticipantStatus } from '../../../interfaces/participants';
import {
  getEstimatedHeadquarterIncome,
  getEstimatedHeadquarterOutflow,
  getEstimatedIncome,
  getEstimatedOutflow,
  getFinalHeadquarterIncome,
  getFinalHeadquarterOutflow,
  getFinalIncome,
  getFinalOutflow,
} from '../../../libs/utils/balancesAggretator';
import sectionsAPIs from '../../../api/section';
import { getCourseLessonsTeacherBudgetTotals } from '../utils/teacherBudgetValidation';

interface Props {
  defaultCourse: CourseAttributes | undefined;
  title: string | undefined;
}

const CostiCorso: React.FC<Props> = ({ title, defaultCourse }) => {
  const { watch, setValue, getValues } = useFormContext<CourseRequest>();
  const [{ profile, token }] = useAuth();

  const lessonsNumber = watch('lessons')?.length || 0;

  const partecipants = watch('partecipations');

  // Ottieni l'ID della sezione selezionata nel form
  const selectedSectionId = watch('section');
  
  // Query per ottenere i dati della sezione quando cambia
  // SOLO durante la creazione di un nuovo corso (quando defaultCourse è undefined)
  const { data: sectionData } = useQuery({
    queryKey: ['section', selectedSectionId],
    queryFn: () => sectionsAPIs.findOne({ 
      id: selectedSectionId, 
      token,
      query: { populate: '*' }
    }),
    enabled: !!selectedSectionId && !!token && !defaultCourse, // Solo se non c'è defaultCourse (creazione)
  });

  // Calcola l'IBAN dalla sezione selezionata o dal defaultCourse
  const sectionIban = useMemo(() => {
    // Se abbiamo un defaultCourse (modifica corso), usa quello
    if (defaultCourse?.section?.data?.attributes?.iban) {
      return defaultCourse.section.data.attributes.iban;
    }
    
    // Altrimenti, usa i dati della sezione ottenuti dalla query (creazione nuovo corso)
    if (sectionData?.data?.attributes?.iban) {
      return sectionData.data.attributes.iban;
    }
    
    return '';
  }, [defaultCourse?.section?.data?.attributes?.iban, sectionData?.data?.attributes?.iban]);

  const minParticipants = watch('minParticipants');
  const originalTeacherBudget = useMemo(
    () =>
      getCourseLessonsTeacherBudgetTotals(
        defaultCourse?.lessons?.data || []
      ),
    [defaultCourse?.lessons?.data]
  );
  const totalConfirmedParticipants = useMemo(
    () =>
      watch('partecipations')?.data.filter(
        (elem) => elem.attributes.status === ParticipantStatus.CONFIRMED
      ).length || 0,
    [watch('partecipations')]
  );

  // Per controllo max partecipanti (corsi): confermati + in attesa
  const totalConfirmedAndPendingParticipants = useMemo(
    () =>
      watch('partecipations')?.data.filter(
        (elem) =>
          elem.attributes.status === ParticipantStatus.CONFIRMED ||
          elem.attributes.status === ParticipantStatus.PENDING
      ).length || 0,
    [watch('partecipations')]
  );

  const updateFoodVoice = () => {
    const food = getValues('balance.food');
    const updatedEstimatedOutflow = food?.estimatedPriceUnit * minParticipants;
    setValue('balance.food.estimatedOutflow', updatedEstimatedOutflow);

    const confirmedParticipants = partecipants?.data?.filter(
      (_participant) =>
        _participant.attributes.status === ParticipantStatus.CONFIRMED
    ).length;

    if (!confirmedParticipants) return;

    const updatedFinalPriceUnit = food?.finalOutflow / confirmedParticipants;
    setValue('balance.food.finalPriceUnit', updatedFinalPriceUnit);
  };

  const updatePriceVoice = () => {
    const isFreeCourse = !!watch('freeCourse');
    if (isFreeCourse) {
      setValue('balance.price.estimatedPriceUnit', 0);
      setValue('balance.price.estimatedIncome', 0);
      return;
    }

    const minParticipants = watch('minParticipants');
    const subscriptionAmount = watch('subscriptionAmount');

    //sottraggo la spesa amministrativa dalla quota corso
    const estimatedPriceUnit = subscriptionAmount - watch('headquarterBalance.administrations.estimatedPriceUnit');
    const estimatedIncome = minParticipants * estimatedPriceUnit;
    setValue('balance.price.estimatedPriceUnit', estimatedPriceUnit);
    setValue('balance.price.estimatedIncome', estimatedIncome);
  };

  const updateRoomsVoice = () => {
    const rooms = getValues('balance.rooms');

    const updatedEstimatedPriceUnit = lessonsNumber
      ? rooms?.estimatedOutflow / lessonsNumber
      : 0;
    setValue('balance.rooms.estimatedPriceUnit', updatedEstimatedPriceUnit);

    const updatedFinalPriceUnit = lessonsNumber
      ? rooms?.finalOutflow / lessonsNumber
      : 0;
    setValue('balance.rooms.finalPriceUnit', updatedFinalPriceUnit);
  };

  const updateExtraVoice = () => {
    const estimatedExtraOutflow =
      getEstimatedOutflow(watch('balance'), ['extras']) * 0.1;

    setValue('balance.extras.estimatedOutflow', estimatedExtraOutflow);
  };

  const updateDirectionVoice = () => {
    const supervisorAmount = watch('supervisorAmount') || 0;
    const treasurerAmount = watch('treasurerAmount') || 0;

    const totDirection = supervisorAmount + treasurerAmount;

    setValue('balance.direction.estimatedOutflow', totDirection);
  };

  const updateTeachingMaterialVoice = () => {
    const teachingMaterial = getValues('headquarterBalance.teachingMaterial');

    const estimatedOutflow =
      (teachingMaterial?.estimatedPriceUnit || 0) * minParticipants;

    const defaultTeachingMaterialsBalance =
      defaultCourse?.headquarterBalance?.teachingMaterial;

    /**
     * SOLO IN MODIFICA
     *
     * Se l'utente modifica il valore delle uscite della quota material didattico,
     * allora bisogna segnarlo attraverso l'attributo isTouched
     *
     * Questo servirà poi quando si sincronizza il bilancio.
     *
     * Se è stato toccato significa che non verrà più calcolato automaticamente.
     */

    if (defaultTeachingMaterialsBalance) {
      const { isTouched, finalOutflow: defaultFinalOutflow } =
        defaultTeachingMaterialsBalance;

      if (
        !isTouched &&
        defaultFinalOutflow !== teachingMaterial?.finalOutflow
      ) {
        setValue('headquarterBalance.teachingMaterial.isTouched', true);
      } else if (!isTouched) {
        setValue('headquarterBalance.teachingMaterial.isTouched', false);
      }
    }

    setValue(
      'headquarterBalance.teachingMaterial.estimatedOutflow',
      estimatedOutflow
    );

    if (partecipants?.data.length) {
      const finalPriceUnit = totalConfirmedParticipants
        ? (teachingMaterial?.finalOutflow || 0) / totalConfirmedParticipants
        : 0;
      setValue(
        'headquarterBalance.teachingMaterial.finalPriceUnit',
        finalPriceUnit
      );
    }
  };

  const updateShippingVoice = () => {
    const shipping = getValues('headquarterBalance.shipping');

    const estimatedPriceUnit =
      (shipping?.estimatedOutflow || 0) / (minParticipants || 0);

    setValue(
      'headquarterBalance.shipping.estimatedPriceUnit',
      estimatedPriceUnit
    );

    if (partecipants?.data.length) {
      const finalPriceUnit = totalConfirmedParticipants
        ? (shipping?.finalOutflow || 0) / totalConfirmedParticipants
        : 0;
      setValue('headquarterBalance.shipping.finalPriceUnit', finalPriceUnit);
    }
  };

  const updateAdministrativeVoice = () => {
    const isFreeCourse = !!watch('freeCourse');
    if (isFreeCourse) {
      setValue('headquarterBalance.administrations.estimatedIncome', 0);
      setValue('headquarterBalance.administrations.estimatedOutflow', 0);
      return;
    }

    const minParticipants = watch('minParticipants');
    const administrations = watch('headquarterBalance.administrations');
    const defaultAdministrationsBalance =
      defaultCourse?.headquarterBalance?.administrations;

    /**
     * SOLO IN MODIFICA
     *
     * Se l'utente modifica il valore delle entrate della quota amministrativa,
     * allora bisogna segnarlo attraverso l'attributo isTouched
     *
     * Questo servirà poi quando si sincronizza il bilancio.
     *
     * Se è stato toccato significa che non verrà più calcolato automaticamente.
     */

    if (defaultAdministrationsBalance) {
      const { isTouched, finalIncome: defaultFinalIncome } =
        defaultAdministrationsBalance;

      if (!isTouched && defaultFinalIncome !== administrations?.finalIncome) {
        setValue('headquarterBalance.administrations.isTouched', true);
      } else if (!isTouched) {
        setValue('headquarterBalance.administrations.isTouched', false);
      }
    }

    const amount = getValues(
      'headquarterBalance.administrations.estimatedPriceUnit'
    );

    const estimatedIncome = amount * minParticipants;

    setValue(
      'headquarterBalance.administrations.estimatedIncome',
      estimatedIncome
    );
    setValue(
      'headquarterBalance.administrations.estimatedOutflow',
      estimatedIncome
    );
  };

  const updateSafetyMarginVoice = () => {
    const headquarterBalance = watch('headquarterBalance');
    const balance = watch('balance');
    const safetyMarginCoefficient = watch('safetyMargin');
    const { estimatedOutflow: balanceEstimatedOutflow } =
      getEntityBalanceTotals(
        { ...(balance || {}), ...(headquarterBalance || {}) },
        ['safetyMargin']
      );

    const estimatedOutflow =
      balanceEstimatedOutflow * Number(safetyMarginCoefficient || 0);

    setValue(
      'headquarterBalance.safetyMargin.estimatedOutflow',
      estimatedOutflow
    );
  };

  const watchedValues = watch([
    'minParticipants',
    'bottlePrice',
    'glassesForBottle',
    'wineTypes',
    'subscriptionAmount',
    'freeCourse',
    'partecipations',
    'treasurerAmount',
    'supervisorAmount',
    'orders.payment',

    'balance.food.estimatedPriceUnit',
    'balance.food.finalOutflow',

    'balance.rooms.estimatedOutflow',
    'balance.rooms.finalOutflow',

    'balance.contributes.estimatedIncome',
    'balance.contributes.finalIncome',
    'balance.discountExpenses.estimatedOutflow',
    'balance.sectionVariousExpenses.estimatedOutflow',
    'balance.sectionVariousExpenses.finalOutflow',
    'balance.couponsExpenses.estimatedOutflow',
    'balance.sponsorship.estimatedOutflow',
    'balance.sponsorship.finalOutflow',
    'balance.teacherPayments.estimatedOutflow',
    'balance.teacherRefunds.estimatedOutflow',

    'balance.extras.finalOutflow',

    'headquarterBalance.teachingMaterial.estimatedPriceUnit',
    'headquarterBalance.teachingMaterial.finalOutflow',

    'headquarterBalance.administrations.estimatedPriceUnit',
    'headquarterBalance.administrations.finalIncome',

    'headquarterBalance.shipping.estimatedOutflow',
    'headquarterBalance.shipping.finalOutflow',
    'headquarterBalance.reimbursement.estimatedOutflow',

    'balance.wines.estimatedOutflow',
    'balance.wines.finalOutflow',
  ]);

  const disableCostsForm = useMemo(
    () =>
      ![CourseStatus.DRAFT, CourseStatus.PUBLISHED, null, undefined].includes(
        watch('status')
      ),
    [watch('status')]
  );

  const updateWinesVoice = () => {
    const bottlePrice = watch('bottlePrice');
    const minParticipants = watch('minParticipants');
    const glassesForBottle = watch('glassesForBottle');
    const wineTypes = watch('wineTypes');
    const finalOutflow = watch('balance.wines.finalOutflow');

    const confirmedParticipants = partecipants?.data?.filter(
      (_participant) =>
        _participant.attributes.status === ParticipantStatus.CONFIRMED
    ).length;

    setValue(
      'balance.wines.estimatedOutflow',
      Math.ceil(minParticipants / (glassesForBottle || 1)) * wineTypes * bottlePrice
    );

    if (!confirmedParticipants) return;

    setValue(
      'balance.wines.finalPriceUnit',
      finalOutflow / confirmedParticipants
    );
  };

  const disableEditConsuntivo = useMemo(
    () => !isCourseBalanceEnabledForEdit(watch('status'), profile),
    [watch('status'), profile]
  );

  useEffect(() => {
    updateWinesVoice();
    updateFoodVoice();
    updatePriceVoice();
    updateRoomsVoice();
    updateDirectionVoice();
    updateTeachingMaterialVoice();
    updateShippingVoice();
    updateAdministrativeVoice();
    updateExtraVoice();
    updateSafetyMarginVoice();
  }, [partecipants, ...watchedValues, defaultCourse]);


  return (
    <>
      <TitlePage title={`${title} | Costi`} />
      <FormCostiCorso
        estimatedIncome={getEstimatedIncome(watch('balance'))}
        estimatedOutflow={getEstimatedOutflow(watch('balance'))}
        finalIncome={getFinalIncome(watch('balance'))}
        finalOutflow={getFinalOutflow(watch('balance'))}
        estimatedHeadquarterOutflow={getEstimatedHeadquarterOutflow(
          watch('headquarterBalance')
        )}
        estimatedHeadquarterIncome={getEstimatedHeadquarterIncome(
          watch('headquarterBalance')
        )}
        finalHeadquarterOutflow={getFinalHeadquarterOutflow(
          watch('headquarterBalance')
        )}
        finalHeadquarterIncome={getFinalHeadquarterIncome(
          watch('headquarterBalance')
        )}
        disabled={disableCostsForm}
        courseStatus={watch('status')}
        safetyMargin={watch('safetyMargin')}
        disableEditConsuntivo={disableEditConsuntivo}
        sectionIban={sectionIban || ''}
        currentConfirmedAndPendingParticipants={totalConfirmedAndPendingParticipants}
        originalMinParticipants={defaultCourse?.minParticipants}
        originalEstimatedTeacherPayment={
          defaultCourse
            ? originalTeacherBudget.estimatedTeacherPayment
            : undefined
        }
        originalEstimatedTeacherRefund={
          defaultCourse
            ? originalTeacherBudget.estimatedTeacherRefund
            : undefined
        }
      />
    </>
  );
};

export default CostiCorso;
